import {Injectable} from "@angular/core";
import {ApiService} from "@core/services/api.service";
import {IBusiness} from "@shared/models";
import {uriAPIConsts, uriConsts} from "@shared/constants";
import {IResponseApi} from "@app/core/models";
import {map} from "rxjs";

/**
 * [§4.7] Một kết nối đại lý (affiliate) — ánh xạ 1:1 từ
 * BE AffiliateBusinessPartnerMapper::parse_hits_2_scroll_customize()
 */
export interface IAffConnectItem {
  id: string;
  business_seller_id: string | null;
  business_seller_name: string | null;
  business_partner_id: string | null;
  business_partner_name: string | null;
  connected_post_count: number;
  shared_post_count: number;
  /** Trạng thái kết nối (enum số từ BE) */
  status: number;
  createdAt: number;
  createdById: string;
}

/** Scroller kết quả phân trang từ Elasticsearch */
export interface IAffConnectScroller {
  items: IAffConnectItem[];
  /** Con trỏ trang tiếp theo; false nếu hết */
  next: any[] | false;
}

/** BE POST /business/affiliate/listconnect → { scroller: IAffConnectScroller } (không qua IResponseApi) */
export interface IAffConnectResponse {
  scroller: IAffConnectScroller;
}

export interface ICreateBusinessParams {
  businessName: string;
  businessPhone: string;
  posUid?: string;
  userEmail?: string;
  userPassword?: string;
}

export interface ICreateBusinessResponse {
  businessId: number;
}

export interface IBusinessSettingItem {
  id: string;
  businessId: number;
  type: number;
  status: number;
  name?: string | null;
  content: string | null;
}

export interface IGetBusinessSettingsParams {
  businessId: number;
  types: (string|number)[];
}

export interface ISaveBusinessSettingByTypeParams {
  businessId: number;
  type: number;
  status: number;
  content: string;
  id?: string;
}

export interface ISavedBusinessSetting extends Omit<IBusinessSettingItem, 'content'> {
  content: object | any[];
  createdById: number;
}

type IBusinessSettingsResponseData =
  | IBusinessSettingItem[]
  | { result?: IBusinessSettingItem[]; data?: IBusinessSettingItem[] };

/**
 * Service xử lý dữ liệu
 * - Thông tin doanh nghiệp
 * */
@Injectable({
  providedIn: 'root'
})
export class BusinessService extends ApiService {

  /**
   * Lấy thông tin của DN
   * */
  getBusiness(id: number) {
    return this.postSystemRequest<IResponseApi<IBusiness>>('uriConsts.store.MANAGE_LOAD', {
      tab: 'getBusiness',
      id: id
    });
  }

  /**
   * Suggest doanh nghiệp user quản lý
   * */
  suggestBusinesses(query: string) {
    return this.postSystemRequest<IResponseApi<IBusiness[]>>('uriConsts.store.MANAGE_LOAD', {
      tab: 'suggest',
      q: query
    });
  }

  createBusiness(params: ICreateBusinessParams) {
    return this.postUserRequest<IResponseApi<ICreateBusinessResponse>>(
      '/business/index/createbusiness',
      params
    );
  }

  /**
   * Lấy danh sách location dùng cho checkout guest flow.
   */
  getShippingLocation(type: 'CITY' | 'DISTRICT' | 'WARD', parentId?: number) {
    return this.postSystemRequest<IResponseApi<any>>('/checkout', {
      act: 'getShippingLocation',
      type,
      ...(parentId ? {parentId} : {}),
    });
  }

  /**
   * [§4.7] Lấy danh sách kết nối đại lý cho chức năng chia sẻ hội thoại.
   */
  listAffConnect(params: { businessId: string | number; type?: number }) {
    return this.postRequest<IAffConnectResponse>('/business/affiliate/listconnect', params);
  }

  /**
   * Lấy cài đặt chia hội thoại (dùng để kiểm tra iOS push notification).
   * Port từ FE13: db.service.ts getBusinessSetting2()
   * @param params { businessId, type } — type=1 = TYPE_CONVERSATION_SPLIT
   */
  getConversationSplitSetting(params: { businessId: string | number; type: number }) {
    return this.postRequest<IResponseApi<{ item: { status: number } }>>(
      uriAPIConsts.business.SETTING2, params
    );
  }

  getBusinessSettings(params: IGetBusinessSettingsParams) {
    return this.postRequest<IResponseApi<IBusinessSettingsResponseData>>(
      uriConsts.business.BUSSINESS_SETTING_LIST, params
    ).pipe(
      map(res => {
        const data = res.data;
        const result = Array.isArray(data)
          ? data
          : Array.isArray(data?.result)
            ? data.result
            : Array.isArray(data?.data)
              ? data.data
              : [];

        return {
          ...res,
          data: {result},
        };
      })
    );
  }

  saveBusinessSettingByType(params: ISaveBusinessSettingByTypeParams) {
    return this.postRequest<IResponseApi<ISavedBusinessSetting>>(
      '/business/setting/savebytype', params
    );
  }

  deleteBusinessSetting(params: { id: string; businessId: number }) {
    return this.postRequest<IResponseApi>('/business/setting/deleteform', params);
  }

}
